/* eslint-disable no-bitwise */
/* eslint-disable @typescript-eslint/no-unused-expressions */
import { RestService } from '../../services/rest.service';
import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  OnInit,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { AuthService } from 'src/app/services/auth.service';
import { Router } from '@angular/router';
import { StorageService } from 'src/app/services/storage.service';
import {
  IonRouterOutlet,
  LoadingController,
  ModalController,
  Platform,
  ViewWillEnter,
  ViewWillLeave,
  AlertController,
} from '@ionic/angular';
import { App } from '@capacitor/app';
import { NetworkService } from './../../services/network.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments';
import { MenuController } from '@ionic/angular';
import { StateService } from 'src/app/services/state.service';
import { filter, take } from 'rxjs/operators';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent
  implements AfterViewChecked, OnInit, ViewWillLeave, ViewWillEnter
{
  public loginForm: UntypedFormGroup;
  public mobileId: string;
  public logging = true;
  public inputPasswordType: 'password' | 'text' = 'password';
  public showError = false;
  isModalOpen = false;
  public selectedLanguage = '';
  public selectedLanguageLabel = '';
  public version = '';
  public permissionsError = '';

  public languageList = [
    { key: 'en', name: 'ADMINISTRATION.USER_PROFILE.LANGUAGES.ENGLISH' },
    { key: 'de', name: 'ADMINISTRATION.USER_PROFILE.LANGUAGES.GERMAN' },
    { key: 'es', name: 'ADMINISTRATION.USER_PROFILE.LANGUAGES.SPANISH' },
    { key: 'it', name: 'ADMINISTRATION.USER_PROFILE.LANGUAGES.ITALIAN' },
    { key: 'nl', name: 'ADMINISTRATION.USER_PROFILE.LANGUAGES.DUTCH' },
    { key: 'pl', name: 'ADMINISTRATION.USER_PROFILE.LANGUAGES.POLISH' },
    { key: 'fr', name: 'ADMINISTRATION.USER_PROFILE.LANGUAGES.FRENCH' },
  ];

  private loadingDialog: HTMLIonLoadingElement;

  constructor(
    public menuCtrl: MenuController,
    fb: UntypedFormBuilder,
    private router: Router,
    private rest: RestService,
    private auth: AuthService,
    private storage: StorageService,
    private loadingController: LoadingController,
    private alertController: AlertController,
    private platform: Platform,
    private routerOutlet: IonRouterOutlet,
    private networkService: NetworkService,
    private translate: TranslateService,
    private modalCtrl: ModalController,
    private stateService: StateService,
    private translatePipe: TranslatePipe
  ) {
    this.loginForm = fb.group({
      userName: '',
      password: '',
      pinCode: '',
      rememberMe: false,
    });

    this.platform.backButton.subscribeWithPriority(-1, () => {
      if (!this.routerOutlet.canGoBack()) {
        App.exitApp();
      }
    });
  }

  async ngOnInit() {
    this.version = environment.version;
    await this.showLoading();

    // Check version first
    this.rest.checkMobileVersion().subscribe({
      next: (isVersionValid) => {
        console.log('isVersionValid => ', isVersionValid);
        if (!isVersionValid.correctVersion) {
          this.loadingDialog.dismiss();
          this.showVersionErrorDialog();
          return;
        }
        this.initializeApp();
      },
      error: (error: any) => {
        console.log('error => ', error);
        this.loadingDialog.dismiss();
        this.showVersionErrorDialog();
      },
    });
  }

  async ngAfterViewChecked() {
    this.permissionsError = this.auth.loginError;
  }

  ionViewWillLeave(): void {
    this.loginForm.setValue({
      userName: '',
      password: '',
      pinCode: '',
      rememberMe: false,
    });
  }

  ionViewWillEnter() {
    this.menuCtrl.enable(false);
    this.storage.get('hashcode').then((val) => {
      if (!val || val.length === 0) {
        this.mobileId = this.generateUUID();
        this.storage.storeMobileId('hashcode', this.mobileId);
      }
    });
  }

  async showLoading() {
    this.loadingDialog = await this.loadingController.create({
      message: this.translatePipe.transform('LOADING'),
    });

    this.loadingDialog.present();
  }

  generateUUID() {
    let dt = new Date().getTime();
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      (c) => {
        const r = (dt + Math.random() * 16) % 16 | 0;
        dt = Math.floor(dt / 16);
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      }
    );
    return uuid;
  }

  public login(): void {
    this.loginForm.markAsTouched();
    this.auth.loginError = '';
    this.permissionsError = '';
    if (this.loginForm.valid) {
      const dto = this.loginForm.getRawValue();
      this.auth.loginNew(dto.rememberMe, dto.userName, dto.password).subscribe(
        (data: any) => {
          console.log('data from Auth => ', data);
          this.showError = false;
          this.stateService
            .waitForFirstTimeLoaded()
            .pipe(take(1))
            .subscribe(() => {
              console.log('navigate to home');
              this.router.navigate(['/home']);
              this.tryToStoreMobileId();
            });
        },
        (error) => {
          this.showError = true;
          this.loginForm.reset({
            userName: '',
            password: '',
            rememberMe: false,
          });
        },
        () => this.loginForm.reset({ rememberMe: false })
      );
    }
  }

  setOpen(isOpen: boolean) {
    this.isModalOpen = isOpen;
  }

  onWillDismiss(modalEvent: Event) {
    this.isModalOpen = false;
    this.storage.get('language').then((val) => {
      if (val && val.length > 0) {
        val = val.replaceAll('"', '');
        this.rest.selectedLanguage = val;
        this.translate.use(val);
      } else {
        this.storage.store('language', 'en');
        this.rest.selectedLanguage = 'en';
        this.translate.use('en');
      }
      this.selectedLanguageLabel = this.languageList.find(
        (language) => language.key === this.rest.selectedLanguage
      )?.name;
    });
  }

  public togglePassword(): void {
    this.inputPasswordType === 'password'
      ? (this.inputPasswordType = 'text')
      : (this.inputPasswordType = 'password');
  }

  public changeLanguage(e) {
    this.translate.use(e);
    this.stateService.setValue('language', e);
    this.rest.selectedLanguage = e;
    this.storage.store('language', e);
  }

  private async showVersionErrorDialog() {
    const alert = await this.alertController.create({
      header: this.translatePipe.transform(
        'LOGIN_PAGE.ERROR.VERSION_ERROR_TITLE'
      ),
      message: this.translatePipe.transform(
        'LOGIN_PAGE.ERROR.VERSION_ERROR_MESSAGE'
      ),
      buttons: [
        {
          text: 'OK',
          handler: () => {
            App.exitApp();
          },
        },
      ],
    });
    await alert.present();
  }

  private async initializeApp() {
    this.storage.get('language').then((val) => {
      if (val && val.length > 0) {
        val = val.replaceAll('"', '');
        this.selectedLanguage = val;
        this.translate.use(val);
      } else {
        this.storage.store('language', 'en');
        this.selectedLanguage = 'en';
        this.translate.use('en');
      }
      this.selectedLanguageLabel = this.languageList.find(
        (language) => language.key === this.selectedLanguage
      )?.name;
    });

    this.storage.get('hashcode').then((val) => {
      if (val && val.length > 0) {
        this.mobileId = val;
        this.auth.loginNewMobile(this.mobileId).subscribe({
          next: (data: any) => {
            this.rest.username = data.name;
            this.logging = false;
            this.loadingDialog.dismiss();
            this.stateService
              .waitForFirstTimeLoaded()
              .pipe(take(1))
              .subscribe(() => {
                this.router.navigate(['/home']);
              });
          },
          error: () => {
            this.logging = false;
            this.loadingDialog.dismiss();
          },
        });
      } else {
        this.mobileId = this.generateUUID();
        this.storage.storeMobileId('hashcode', this.mobileId);
        this.logging = false;
        this.loadingDialog.dismiss();
      }
    });
  }

  private tryToStoreMobileId() {
    this.auth.addMobileId(this.mobileId).subscribe({
      next: (val: any) => {
        console.log('next => ', val);
      },
      error: (error: any) => {
        console.log('error => ', error);
      },
    });
  }
}
